import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { TextField, Button, Container, Typography, Box, Paper, Grid, IconButton, CircularProgress } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';

const Login = () => {
  const { login, isLoggingIn } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await login(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
      py: { xs: 4, md: 8 },
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 0% 0%, rgba(92,15,47,0.05) 0%, transparent 50%)',
        zIndex: 0
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        right: '-10%',
        width: '40%',
        height: '40%',
        background: 'radial-gradient(circle, rgba(92,15,47,0.1) 0%, transparent 70%)',
        transform: 'translateY(-50%)',
        zIndex: 0
      }
    }}>
      {/* Navigation Buttons */}
      <Box sx={{ 
        position: 'absolute',
        top: 20,
        left: 20,
        zIndex: 10,
        display: 'flex',
        gap: 2
      }}>
        <IconButton 
          onClick={() => navigate('/')}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { 
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <HomeIcon />
        </IconButton>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { 
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              textAlign: 'center',
              mb: { xs: 4, md: 0 }
            }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 800,
                  color: '#333',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 10px rgba(92,15,47,0.2)'
                }}
              >
                Welcome Back
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#666',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                Sign in to continue your journey to find your perfect match
              </Typography>
              <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                mb: 4
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <VerifiedUserIcon />
                  <Typography>Secure Login</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <SecurityIcon />
                  <Typography>Privacy Protected</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <FavoriteIcon />
                  <Typography>Quick Access</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 3, md: 4 },
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                }
              }}
            >
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 700,
                  color: '#333',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #5c0f2f, #7d1f4f)',
                    borderRadius: '2px'
                  }
                }}
              >
                Sign In
              </Typography>
              {error && (
                <Typography 
                  color="error" 
                  sx={{ 
                    textAlign: 'center',
                    mb: 2,
                    p: 1,
                    bgcolor: 'rgba(255, 0, 0, 0.1)',
                    borderRadius: '4px'
                  }}
                >
                  {error}
                </Typography>
              )}
              <Box 
                component="form" 
                onSubmit={handleSubmit(onSubmit)} 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <TextField
                  fullWidth
                  label="Email Address"
                  autoComplete="email"
                  autoFocus
                  {...register('email', { required: 'Email is required' })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password', { required: 'Password is required' })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    startAdornment: <LockIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#5c0f2f' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <Button 
                  type="submit" 
                  fullWidth 
                  variant="contained" 
                  disabled={isLoggingIn}
                  sx={{ 
                    mt: 2,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      opacity: 0.7
                    },
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    transition: 'all 0.3s'
                  }}
                >
                  {isLoggingIn ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      Signing In...
                    </Box>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <Box sx={{ 
                  textAlign: 'center',
                  mt: 2
                }}>
                  <Box sx={{ 
                    p: 2,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.08) 0%, rgba(125,31,79,0.08) 100%)',
                    border: '1px solid rgba(92,15,47,0.2)',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(92,15,47,0.15)',
                      borderColor: 'rgba(92,15,47,0.4)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.12) 0%, rgba(125,31,79,0.12) 100%)'
                    }
                  }}>
                    <Link 
                      to="/register" 
                      style={{ 
                        color: '#5c0f2f',
                        textDecoration: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#7d1f4f';
                        e.target.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#5c0f2f';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      ✨ Don't have an account? Sign Up
                    </Link>
                  </Box>
                  <Box sx={{ 
                    mt: 2,
                    p: 2,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.1) 0%, rgba(125,31,79,0.1) 100%)',
                    border: '2px solid rgba(92,15,47,0.3)',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(92,15,47,0.2)',
                      borderColor: 'rgba(92,15,47,0.6)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.15) 0%, rgba(125,31,79,0.15) 100%)'
                    }
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#5c0f2f',
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '0.9rem'
                      }}
                    >
                      Want to become a Partner?
                    </Typography>
                    <a 
                      href="https://seethakalyanavaibhogame.com/skvadmin/partner-register"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        textDecoration: 'none',
                        fontSize: '1rem',
                        fontWeight: 700,
                        padding: '8px 16px',
                        borderRadius: '8px',
                        background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                        color: 'white',
                        transition: 'all 0.3s',
                        boxShadow: '0 2px 8px rgba(92,15,47,0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 4px 15px rgba(92,15,47,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 2px 8px rgba(92,15,47,0.3)';
                      }}
                    >
                      🚀 Register as a Partner
                    </a>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Login;